import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Send, FileText, ChevronDown, CheckCircle2 } from "lucide-react";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function IstatC59() {
  const today = new Date();
  // Default: previous month (typical regulatory cadence — by the 5th of next month).
  const defaultMonth = today.getDate() <= 10
    ? new Date(today.getFullYear(), today.getMonth() - 1)
    : new Date(today.getFullYear(), today.getMonth());
  const [year, setYear] = useState(defaultMonth.getFullYear());
  const [month, setMonth] = useState(defaultMonth.getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pv }, { data: fs }] = await Promise.all([
        api.get(`/istat-c59/preview?year=${year}&month=${month}`),
        api.get("/istat-c59/filings"),
      ]);
      setPreview(pv);
      setFilings(fs);
    } catch (err) {
      if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/istat-c59/export?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `istat_c59_${year}_${String(month).padStart(2, "0")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore download"); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/istat-c59/send?year=${year}&month=${month}`);
      toast.success(data.message || "Invio simulato OK");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSending(false); }
  };

  const isFiled = filings.some(f => f.year === year && f.month === month);
  const years = Array.from({ length: 4 }, (_, i) => today.getFullYear() - i);

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">ADEMPIMENTI · ISTAT C59</p>
      <h1 className="font-serif text-4xl tracking-tight mb-2">Statistica del turismo (MOD ISTAT C59)</h1>
      <p className="text-stone-600 mb-8 max-w-prose">
        Riepilogo mensile di arrivi e presenze per nazionalità da inviare al portale regionale (Lombardia TURI, Lazio RADAR, Veneto Gestionale Presenze, ecc.). Xenia genera il CSV nel formato accettato dalla maggior parte dei portali.
      </p>

      {/* Picker */}
      <div className="flex flex-wrap gap-3 items-end mb-8">
        <label className="block">
          <span className="text-xs tracked text-stone-500 block mb-1">MESE</span>
          <div className="relative">
            <select value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="istat-month"
              className="appearance-none border border-stone-300 px-4 py-2 pr-10 bg-white text-sm focus:outline-none focus:border-blue-700">
              {MONTHS_IT.map((m, i) => <option key={`m-${i+1}`} value={i + 1}>{m}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-stone-400" />
          </div>
        </label>
        <label className="block">
          <span className="text-xs tracked text-stone-500 block mb-1">ANNO</span>
          <div className="relative">
            <select value={year} onChange={e => setYear(Number(e.target.value))} data-testid="istat-year"
              className="appearance-none border border-stone-300 px-4 py-2 pr-10 bg-white text-sm focus:outline-none focus:border-blue-700">
              {years.map(y => <option key={`y-${y}`} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-stone-400" />
          </div>
        </label>
        <div className="ml-auto flex gap-2">
          <button onClick={downloadCsv} disabled={loading || !preview} data-testid="istat-download"
            className="inline-flex items-center gap-2 border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50 disabled:opacity-50">
            <Download className="w-4 h-4" /> Scarica CSV
          </button>
          <button onClick={send} disabled={loading || sending || !preview} data-testid="istat-send"
            className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2 text-sm rounded-sm hover:bg-stone-800 disabled:opacity-50">
            <Send className="w-4 h-4" /> {sending ? "Invio…" : "Invia al portale"}
          </button>
        </div>
      </div>

      {isFiled && (
        <div className="mb-6 inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          Periodo già inviato (mock). Puoi rigenerare il CSV in qualsiasi momento.
        </div>
      )}

      {/* KPI */}
      {preview && (
        <div className="grid sm:grid-cols-4 gap-4 mb-8" data-testid="istat-kpis">
          <Stat label="Arrivi" value={preview.totals.arrivals} />
          <Stat label="Presenze" value={preview.totals.nights} />
          <Stat label="Camere" value={preview.rooms} />
          <Stat label="Occupazione media" value={`${preview.occupancy_pct}%`} />
        </div>
      )}

      {/* Table */}
      <div className="border border-stone-200 bg-white">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-700" />
          <span className="text-sm font-medium">Riepilogo per nazionalità</span>
        </div>
        {loading ? (
          <p className="p-6 text-stone-500 text-sm">…</p>
        ) : !preview || preview.rows.length === 0 ? (
          <p className="p-6 text-stone-500 text-sm" data-testid="istat-empty">
            Nessuna prenotazione registrata in questo mese.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="istat-table">
            <thead className="bg-stone-50 text-xs tracked text-stone-500">
              <tr>
                <th className="text-left px-4 py-2 border-b border-stone-200">PAESE</th>
                <th className="text-right px-4 py-2 border-b border-stone-200">ARRIVI</th>
                <th className="text-right px-4 py-2 border-b border-stone-200">PRESENZE</th>
                <th className="text-right px-4 py-2 border-b border-stone-200">PERMANENZA MEDIA</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map(r => (
                <tr key={r.country_iso2} className="border-b border-stone-100">
                  <td className="px-4 py-2">{r.country_iso2}</td>
                  <td className="px-4 py-2 text-right">{r.arrivals}</td>
                  <td className="px-4 py-2 text-right">{r.nights}</td>
                  <td className="px-4 py-2 text-right">{r.arrivals ? (r.nights / r.arrivals).toFixed(1) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="font-medium bg-stone-50">
              <tr>
                <td className="px-4 py-2">Totale</td>
                <td className="px-4 py-2 text-right">{preview.totals.arrivals}</td>
                <td className="px-4 py-2 text-right">{preview.totals.nights}</td>
                <td className="px-4 py-2 text-right">
                  {preview.totals.arrivals ? (preview.totals.nights / preview.totals.arrivals).toFixed(1) : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* History */}
      {filings.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl mb-4">Cronologia invii</h2>
          <div className="border border-stone-200 bg-white divide-y divide-stone-200" data-testid="istat-filings">
            {filings.map(f => (
              <div key={`${f.year}-${f.month}`} className="px-4 py-3 flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-700" />
                <span className="font-medium">{MONTHS_IT[f.month - 1]} {f.year}</span>
                <span className="text-stone-500 text-xs">
                  {f.totals?.arrivals ?? 0} arrivi · {f.totals?.nights ?? 0} presenze
                </span>
                <span className="ml-auto text-stone-400 text-xs">
                  {f.sent_at ? new Date(f.sent_at).toLocaleString("it-IT") : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-stone-200 bg-white p-4">
      <div className="text-xs tracked text-stone-500 mb-1">{label.toUpperCase()}</div>
      <div className="font-serif text-3xl">{value}</div>
    </div>
  );
}
